import { useEffect, useState } from "react";
import { NegotiationPackTable } from "../NegotiationPack";
import { NegotiationPackDetails, type NegotiationPackTab } from "./NegotiationPackDetails";

type View = "list" | "loading" | "detail" | "generating";
type Supplier = Parameters<React.ComponentProps<typeof NegotiationPackTable>["onOpen"]>[0];

export function NegotiationPackView() {
  const [view, setView] = useState<View>("list");
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [tab, setTab] = useState<NegotiationPackTab>("overview");
  const [modalOpen, setModalOpen] = useState(false);

  const openSupplier = (item: Supplier) => {
    setSupplier(item);
    setView("loading");
  };

  return (
    <div className="npv-root">
      <main className="npv-main">
        {view === "list" && <NegotiationPackTable onOpen={openSupplier} />}
        {view === "loading" && (
          <LoadingView
            supplier={supplier}
            onCancel={() => setView("list")}
            onDone={() => {
              setTab("overview");
              setView("detail");
            }}
          />
        )}
        {view === "generating" && (
          <GeneratingView
            onDone={() => {
              setTab("meetingpack");
              setView("detail");
            }}
          />
        )}
        {view === "detail" && <DetailView tab={tab} setTab={setTab} onSkip={() => setModalOpen(true)} />}
        {modalOpen && (
          <SkipModal
            onClose={() => setModalOpen(false)}
            onSkip={() => {
              setModalOpen(false);
              setView("generating");
            }}
          />
        )}
      </main>
    </div>
  );
}

function DetailView({
  tab,
  setTab,
  onSkip,
}: {
  tab: NegotiationPackTab;
  setTab: (tab: NegotiationPackTab) => void;
  onSkip: () => void;
}) {
  return (
    <div className="npv-stack">
      <div>
        <div className="npv-eyebrow">Sourcing</div>
        <div className="npv-title">Negotiation Pack</div>
      </div>

      <NegotiationPackDetails tab={tab} setTab={setTab} onSkip={onSkip} />
    </div>
  );
}

function LoadingView({
  supplier,
  onCancel,
  onDone,
}: {
  supplier: Supplier | null;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [progress, setProgress] = useState(8);
  const steps = [
    "Analyzing supplier data and spend history",
    "Building negotiation strategy",
    "Creating playbook and battle cards",
    "Running scenario simulations",
  ];

  useEffect(() => {
    const startedAt = performance.now();
    let frame = 0;
    let finishTimer = 0;
    const update = (now: number) => {
      const value = Math.min(100, Math.round(8 + ((now - startedAt) / 3200) * 92));
      setProgress(value);
      if (value < 100) frame = window.requestAnimationFrame(update);
      else finishTimer = window.setTimeout(onDone, 500);
    };
    frame = window.requestAnimationFrame(update);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(finishTimer);
    };
  }, [onDone]);

  const activeStep = progress < 30 ? 0 : progress < 55 ? 1 : progress < 85 ? 2 : 3;

  return (
    <div className="npv-stack">
      <div>
        <div className="npv-eyebrow">Sourcing</div>
        <div className="npv-title">Negotiation Pack</div>
      </div>

      <section className="npv-loading-card">
        <div className="npv-loading-inner">
          <div className="npv-loading-supplier">
            <div className="npv-loading-supplier-name">{supplier?.name || "CastPro"}</div>
            <div className="npv-loading-supplier-loc">{supplier?.loc || "Monterrey, MX"}</div>
          </div>

          <div className="npv-loading-divider" />

          <div className="npv-loading-spinner" aria-hidden="true">
            <span className="npv-loading-spinner-mark">✧</span>
          </div>

          <div className="npv-loading-copy">
            <div className="npv-loading-title">Preparing your negotiation brief</div>
            <div className="npv-loading-sub">This usually takes 30-60 seconds.</div>
          </div>

          <div className="npv-step-list">
            {steps.map((label, index) => {
              const complete = index < activeStep;
              const current = index === activeStep;
              return (
                <div
                  key={label}
                  className={`npv-step-row${index > activeStep ? " is-dimmed" : ""}`}
                >
                  <div
                    className={`npv-step-dot${
                      complete ? " is-complete" : current ? " is-current" : ""
                    }${index > activeStep ? " is-upcoming" : ""}`}
                  >
                    {complete ? <span className="npv-step-check">✓</span> : current ? <span className="npv-loading-dot" /> : null}
                  </div>
                  <span
                    className={`npv-step-label${
                      current ? " is-current" : complete ? " is-complete" : ""
                    }`}
                  >
                    {label}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="npv-progress-block">
            <progress className="npv-progress" value={progress} max={100} />
            <div className="npv-progress-meta">
              <span>{progress}% completed</span>
              <span>~{Math.max(0, Math.round((100 - progress) * 0.18))}s remaining</span>
            </div>
          </div>

          <button type="button" onClick={onCancel} className="npv-cancel-btn">
            Cancel
          </button>
        </div>
      </section>
    </div>
  );
}

function GeneratingView({ onDone }: { onDone: () => void }) {
  useEffect(() => {
    const timer = window.setTimeout(onDone, 15000);
    return () => window.clearTimeout(timer);
  }, [onDone]);

  return (
    <section className="npv-generating-card">
      <div className="npv-generating-spinner" aria-hidden="true">
        <span>✦</span>
      </div>
      <h1 className="npv-generating-title">Generating your meeting pack</h1>
      <p className="npv-generating-sub">Compiling strategy, talking points, and concession roadmap</p>
      <div className="npv-generating-chip">Estimated time remaining: 15 seconds</div>
      <p className="npv-generating-note">Your overview selections are being applied to the meeting pack.</p>
    </section>
  );
}

function SkipModal({ onClose, onSkip }: { onClose: () => void; onSkip: () => void }) {
  return (
    <div role="dialog" aria-modal="true" aria-labelledby="skip-meeting-pack-title" className="npv-modal-overlay">
      <div className="npv-modal-card">
        <div className="npv-modal-head">
          <h2 id="skip-meeting-pack-title" className="npv-modal-title">Skip to Meeting Pack?</h2>
          <button type="button" aria-label="Close dialog" onClick={onClose} className="npv-modal-close">
            ×
          </button>
        </div>

        <p className="npv-modal-body">
          The meeting pack will be generated using AI-recommended default settings based on active contract terms and benchmarking indices. You can always return to customise your strategy, playbooks, and negotiation scenarios later.
        </p>

        <div className="npv-modal-actions">
          <button type="button" onClick={onClose} className="npv-btn npv-btn--outline npv-btn--modal">
            Go Back
          </button>
          <button type="button" onClick={onSkip} className="npv-btn npv-btn--gradient npv-btn--modal">
            Skip to Meeting Pack <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </div>
  );
}
